import { useState, useEffect } from "react";
import { PanelLeft } from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import { useNavigation } from "@/contexts/NavigationContext";
import { useFavorites } from "@/hooks/useFavorites";
import { useSharedProjects } from "@/hooks/useProjects";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import caldeiraLogo from "@/assets/caldeira-logo.png";
import caldeiraLogoWhite from "@/assets/caldeira-logo-white.png";
import { Accordion } from "@/components/ui/accordion";
import { SidebarSectionFavorites } from "./sidebar/SidebarSectionFavorites";
import { SidebarSectionShared } from "./sidebar/SidebarSectionShared";
import { SidebarSectionCaldeira } from "./sidebar/SidebarSectionCaldeira";
import { SidebarSectionPersonal } from "./sidebar/SidebarSectionPersonal";
import { SidebarSectionHistory } from "./sidebar/SidebarSectionHistory";

export const Sidebar = () => {
  const {
    navigation,
    navigateToGlobal,
    sidebarCollapsed,
    setSidebarCollapsed,
    activeConversationId,
  } = useNavigation();
  const { theme } = useTheme();
  const isMobile = useIsMobile();

  const { data: favorites } = useFavorites();
  const { data: sharedProjects } = useSharedProjects();

  const hasFavorites = favorites && favorites.length > 0;
  const hasShared = sharedProjects && sharedProjects.length > 0;

  // Track content visibility separately for fade-before-collapse effect
  const [contentVisible, setContentVisible] = useState(!sidebarCollapsed);

  const handleCollapse = () => {
    if (isMobile) {
      setSidebarCollapsed(true);
      setContentVisible(false);
    } else {
      setContentVisible(false);
      setTimeout(() => setSidebarCollapsed(true), 150);
    }
  };

  // Auto-close sidebar on mobile when navigation changes
  useEffect(() => {
    if (isMobile && !sidebarCollapsed) {
      setSidebarCollapsed(true);
      setContentVisible(false);
    }
  }, [navigation.tab, navigation.projectId, navigation.isPersonal, activeConversationId]);

  useEffect(() => {
    if (!sidebarCollapsed) {
      const timer = setTimeout(() => setContentVisible(true), 50);
      return () => clearTimeout(timer);
    }
  }, [sidebarCollapsed]);

  // On mobile, don't render if collapsed
  if (isMobile && sidebarCollapsed) return null;

  return (
    <>
      {/* Backdrop for mobile */}
      {isMobile && !sidebarCollapsed && (
        <div
          className="fixed inset-0 bg-black/50 z-40 animate-in fade-in-0 duration-200"
          onClick={handleCollapse}
        />
      )}
      <aside
        className={cn(
          "fixed left-0 top-0 bottom-0 bg-sidebar border-r border-color z-50 flex flex-col overflow-hidden",
          isMobile
            ? "w-full max-w-sm animate-in slide-in-from-left duration-300"
            : cn("transition-[width] duration-300", sidebarCollapsed ? "w-0" : "w-64"),
        )}
      >
        <div
          className={cn(
            "flex flex-col h-full transition-opacity duration-150",
            isMobile ? "min-w-0" : "min-w-[16rem]"
          )}
          style={{ opacity: contentVisible ? 1 : 0 }}
        >
          {/* Header */}
          <div className="h-[60px] px-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigateToGlobal()}>
              <img src={theme === "dark" ? caldeiraLogoWhite : caldeiraLogo} alt="Caldeira AI" className="h-8 w-auto" />
            </div>
            <button
              onClick={handleCollapse}
              className="h-8 w-8 flex items-center justify-center rounded-md text-secondary hover:bg-hover hover:text-primary transition-colors"
              title="Fechar sidebar"
            >
              <PanelLeft className="h-4 w-4" />
            </button>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <SidebarSectionFavorites />
            <SidebarSectionShared />

            {(hasFavorites || hasShared) && <div className="border-t border-color" />}

            <Accordion type="multiple" defaultValue={["pessoal", "historico"]} className="space-y-1">
              <SidebarSectionCaldeira />
              <SidebarSectionPersonal />
              <SidebarSectionHistory />
            </Accordion>
          </div>
        </div>
      </aside>
    </>
  );
};
